import { defineStore } from 'pinia';
import { fetchGroupById } from '@/queries/groups/useGroupQuery';
import { queryClient } from '@/queries/_shared/queryClient';
import { queryKeys } from '@/queries/_shared/queryKeys';

const GROUP_INFO_REFRESH_TIMES_KEY = 'viewerGroupInfoRefreshTimes';
const GROUP_INFO_REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000;

function parseJson(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function unwrapGroup(data) {
  return data?.body || data?._data || data || {};
}

function getGroupRefreshKey(groupId) {
  const institutionId = localStorage.getItem('institution-id') || 'unknown';
  return `${institutionId}:${String(groupId)}`;
}

function shouldRefreshGroupInfo(groupId) {
  const refreshTimes = parseJson(localStorage.getItem(GROUP_INFO_REFRESH_TIMES_KEY) || '{}', {});
  const refreshedAt = Number(refreshTimes?.[getGroupRefreshKey(groupId)] || 0);
  const now = Date.now();

  return !refreshedAt || refreshedAt > now || now - refreshedAt >= GROUP_INFO_REFRESH_INTERVAL;
}

function markGroupInfoRefreshed(groupId) {
  const refreshTimes = parseJson(localStorage.getItem(GROUP_INFO_REFRESH_TIMES_KEY) || '{}', {});
  refreshTimes[getGroupRefreshKey(groupId)] = Date.now();
  localStorage.setItem(GROUP_INFO_REFRESH_TIMES_KEY, JSON.stringify(refreshTimes));
}

function normalizeChildren(rawChildren) {
  if (!Array.isArray(rawChildren)) return [];

  return rawChildren
    .map((child) => ({
      uin: String(child?.uin ?? ''),
      first_name: child?.first_name || '',
      middle_name: child?.middle_name || '',
      sex: Number(child?.sex || 0),
      server_id: child?.server_id || child?._data?.server_id || child?._server?.id || child?.server?.id || null,
      region_id: child?._server?.region_id || child?.server?.region_id || null,
      pupil_id: child?.pupil_id || child?._data?.pupil_id || null,
      group_id: child?.group_id || child?.group?.id || null,
      group_name: child?.group?.name || child?.group_name || '',
      group_course: child?.group_course || child?.course || child?.group?.course || null,
      class_number:
        child?.class_number ?? child?.group_course ?? child?.course ?? child?.group?.course ?? null,
      meals_privilege_type:
        child?.meals_privilege_type ?? child?._data?.meals_privilege_type ?? null,
      avatar: child?.avatar?.small || child?.photo?.small || ''
    }))
    .filter((child) => child.uin);
}

export const useViewerStore = defineStore('viewer', {
  state: () => ({
    role: 'parent',
    profile: {},
    children: [],
    selectedChildUin: null
  }),
  getters: {
    isParent: (state) => state.role === 'parent',
    isChild: (state) => state.role === 'child',
    currentChildUin(state) {
      if (state.role === 'child') {
        const ownUin = state.profile?.uin;
        return ownUin ? String(ownUin) : null;
      }
      return state.selectedChildUin;
    },
    currentChildRouteId() {
      return this.currentChildUin || 'me';
    },
    selectedChild(state) {
      if (!state.selectedChildUin) return null;
      return state.children.find((child) => child.uin === state.selectedChildUin) || null;
    }
  },
  actions: {
    async fetchGroupInfo(groupId, { force = false } = {}) {
      if (!groupId) return null;
      try {
        const queryKey = queryKeys.groups.byId(groupId);
        if (force) {
          await queryClient.invalidateQueries({ queryKey });
        }
        const data = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => fetchGroupById(groupId),
          ...(force ? { staleTime: 0 } : {})
        });
        const group = unwrapGroup(data);
        return {
          id: group?.id || groupId,
          name: group?.name || group?.title || '',
          class_number: group?.course ?? null
        };
      } catch {
        return null;
      }
    },
    async refreshChildGroupInfo(uin = this.selectedChildUin) {
      if (this.role !== 'parent') return;
      const targetUin = String(uin || '');
      if (!targetUin) return;

      const childIndex = this.children.findIndex((child) => String(child.uin) === targetUin);
      const child = this.children[childIndex];
      const groupId = child?.group_id || child?.group?.id;
      if (childIndex === -1 || !groupId) return;

      try {
        const queryKey = queryKeys.groups.byId(groupId);
        await queryClient.invalidateQueries({ queryKey });
        const group = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => fetchGroupById(groupId),
          staleTime: 0
        });
        const groupRow = unwrapGroup(group);
        const classNumber = groupRow?.course ?? null;
        const currentChildIndex = this.children.findIndex((item) => String(item?.uin) === targetUin);
        if (currentChildIndex === -1) return;
        if (Number(this.children[currentChildIndex]?.group_id || 0) !== Number(groupId)) return;

        const nextChildren = [...this.children];
        const currentChild = nextChildren[currentChildIndex];
        nextChildren[currentChildIndex] = {
          ...currentChild,
          group_id: groupRow.id || groupId,
          group_name: groupRow.name || groupRow.title || currentChild.group_name || '',
          group_course: groupRow.course || groupRow.parallel || currentChild.group_course || null,
          class_number: classNumber ?? currentChild.class_number ?? null,
          group: {
            ...(currentChild.group || {}),
            ...groupRow,
            id: groupRow.id || groupId,
            name: groupRow.name || groupRow.title || currentChild.group_name || ''
          }
        };
        this.children = nextChildren;
        this.persistChildrenToStorage();
      } catch {
        // keep selected child usable even when group refresh fails
      }
    },
    persistProfileToStorage() {
      const person = parseJson(localStorage.getItem('person') || '{}', {});
      person.profile = this.profile || {};
      localStorage.setItem('person', JSON.stringify(person));
    },
    persistChildrenToStorage() {
      const rawChildren = parseJson(localStorage.getItem('childList') || '[]', []);
      if (!Array.isArray(rawChildren) || rawChildren.length === 0) return;

      const next = rawChildren.map((item) => {
        const normalized = this.children.find((child) => String(child.uin) === String(item?.uin));
        if (!normalized) return item;

        return {
          ...item,
          sex: normalized.sex || item?.sex || 0,
          server_id: normalized.server_id || item?.server_id || item?._data?.server_id || item?._server?.id || item?.server?.id || null,
          group_id: normalized.group_id,
          group_name: normalized.group_name,
          group_course: normalized.group_course || item?.group_course || item?.course || item?.group?.course || null,
          class_number: normalized.class_number ?? item?.class_number ?? null,
          group: {
            ...(item?.group || {}),
            id: normalized.group_id || item?.group?.id,
            name: normalized.group_name || item?.group?.name || '',
            course: normalized.group_course || item?.group?.course || null
          }
        };
      });

      localStorage.setItem('childList', JSON.stringify(next));
    },
    initFromStorage() {
      this.role = localStorage.getItem('currentRole') || 'parent';

      const person = parseJson(localStorage.getItem('person') || '{}', {});
      const profile = person?.profile || {};
      this.profile = {
        ...profile,
        class_number:
          profile?.class_number ?? profile?.group_course ?? profile?.course ?? profile?.group?.course ?? null
      };
      if (!this.profile.region_id && person?.server?.region_id) {
        this.profile = {
          ...this.profile,
          region_id: person.server.region_id
        };
      }

      const parsedChildren = normalizeChildren(parseJson(localStorage.getItem('childList') || '[]', []));
      this.children = parsedChildren;

      if (this.role === 'parent') {
        const stored = localStorage.getItem('currentChildUin');
        const firstUin = this.children[0]?.uin || null;
        const hasStored = stored && this.children.some((child) => child.uin === String(stored));
        this.selectedChildUin = hasStored ? String(stored) : firstUin;

        if (this.selectedChildUin) {
          localStorage.setItem('currentChildUin', this.selectedChildUin);
        } else {
          localStorage.removeItem('currentChildUin');
        }
        return;
      }

      const ownUin = this.profile?.uin ? String(this.profile.uin) : null;
      this.selectedChildUin = ownUin;
      if (ownUin) {
        localStorage.setItem('currentChildUin', ownUin);
      } else {
        localStorage.removeItem('currentChildUin');
      }
    },
    async hydrateMissingGroupInfo() {
      if (this.role === 'child') {
        const hasGroupName = !!(this.profile?.group?.name || this.profile?.group_name);
        const hasClassNumber = this.profile?.class_number !== null && this.profile?.class_number !== undefined;
        const groupId = this.profile?.group_id || this.profile?.group?.id;
        if (!groupId) return;

        const refreshDue = shouldRefreshGroupInfo(groupId);
        if (hasGroupName && hasClassNumber && !refreshDue) return;

        const groupInfo = await this.fetchGroupInfo(groupId, { force: refreshDue });
        if (!groupInfo) return;

        this.profile = {
          ...this.profile,
          group_id: groupId,
          group_name: groupInfo.name || this.profile?.group_name || '',
          group_course: groupInfo.class_number ?? this.profile?.group_course ?? null,
          class_number: groupInfo.class_number ?? this.profile?.class_number ?? null,
          group: {
            ...(this.profile?.group || {}),
            id: groupId,
            name: groupInfo.name || this.profile?.group?.name || '',
            course: groupInfo.class_number ?? this.profile?.group?.course ?? null
          }
        };
        this.persistProfileToStorage();
        markGroupInfoRefreshed(groupId);
        return;
      }

      if (!this.isParent || !this.children.length) return;

      const requests = this.children.flatMap((child) => {
        const hasClassNumber = child?.class_number !== null && child?.class_number !== undefined;
        if (!child?.group_id) return [];

        const uin = String(child.uin);
        const groupId = child.group_id;
        const refreshDue = shouldRefreshGroupInfo(groupId);
        if (child?.group_name && hasClassNumber && !refreshDue) return [];

        return [
          this.fetchGroupInfo(groupId, { force: refreshDue }).then((groupInfo) => ({ uin, groupId, groupInfo }))
        ];
      });
      const results = await Promise.all(requests);
      const nextChildren = [...this.children];
      let changed = false;

      results.forEach(({ uin, groupId, groupInfo }) => {
        if (!groupInfo) return;
        const childIndex = nextChildren.findIndex((child) => String(child?.uin) === uin);
        if (childIndex === -1) return;

        const child = nextChildren[childIndex];
        if (Number(child?.group_id || 0) !== Number(groupId)) return;

        nextChildren[childIndex] = {
          ...child,
          group_name: groupInfo.name || child.group_name || '',
          group_course: groupInfo.class_number ?? child.group_course ?? null,
          class_number: groupInfo.class_number ?? child.class_number ?? null,
          group: {
            ...(child.group || {}),
            id: groupInfo.id || groupId,
            name: groupInfo.name || child.group?.name || '',
            course: groupInfo.class_number ?? child.group?.course ?? null
          }
        };
        markGroupInfoRefreshed(groupId);
        changed = true;
      });

      if (!changed) return;
      this.children = nextChildren;
      this.persistChildrenToStorage();
    },
    selectChild(uin) {
      if (this.role !== 'parent') return;
      const nextUin = String(uin || '');
      if (!nextUin) return;

      const child = this.children.find((item) => item.uin === nextUin);
      if (!child) return;

      this.selectedChildUin = nextUin;
      localStorage.setItem('currentChildUin', nextUin);
      if (!child.group_name || child.class_number === null || child.class_number === undefined) {
        void this.refreshChildGroupInfo(nextUin);
      }
    }
  }
});
